/**
 * API 錯誤 → 使用者可見訊息的「轉譯層」。
 *
 * 原則:**不把原始錯誤直接呈現給使用者。**
 * - 系統/認證/網路錯誤(5xx / 401 / 403 / 逾時 / 斷線):一律用前端制式文案,
 *   絕不顯示後端 body(可能含 stack trace、SQL、內部路徑等敏感/技術資訊)。
 * - 業務性 4xx(400 / 409 / 422):這類錯誤後端「刻意」回給使用者看(如刪除阻擋原因、
 *   逐欄驗證訊息、「資產編號『X』已存在」),採用後端 message 或純字串原因;沒有則給該狀態的制式文案。
 * - 原始錯誤一律 console.error 保留給開發者除錯(見 api_helper 攔截器)。
 *
 * 未來:後端 Swagger 若提供錯誤 `code`,改為 code → i18n 對照(比信任 message 文字更穩)。
 */

const GENERIC = "系統發生錯誤,請稍後再試";
/** 制式「系統錯誤」文案(呼叫端拿到的不是可顯示字串時的退路) */
export const GENERIC_ERROR_MSG = GENERIC;

// 網路層文案輸出成常數:防護測試(hybridPassthrough)以此判斷「請求是否出網」,
// 引用同一份常數,改文案不會讓防護測試靜默失明
export const NETWORK_ERROR_MSG = "網路連線異常,請檢查網路後再試";
export const TIMEOUT_MSG = "連線逾時,請稍後再試";
export const NOT_IMPLEMENTED_MSG = "此功能尚未串接正式後端";

// 純字串 body 的技術訊息特徵(例外類別名、堆疊行、SQL、ASP.NET 預設的英文欄位驗證句
// 如「The username field is required.」—— 欄位名是程式名,不是給使用者看的原因)
const LOOKS_TECHNICAL = /Exception\b|\n\s*at\s|\bat\s+[\w.$]+\(|\bSQL\b|SQLSTATE|System\.[A-Z]|\bfield is required\b|could not be converted|is not valid for/i;

// 後端 message 僅在這些「業務性」狀態才可信任呈現
const TRUST_BACKEND_MESSAGE = new Set([400, 409, 422]);

// 制式文案(不使用後端 body)
const BY_STATUS = {
  400: "資料格式有誤,請確認後再試",
  401: "登入已失效,請重新登入",
  403: "您沒有權限執行此操作",
  404: "查無資料",
  408: TIMEOUT_MSG,
  409: "操作衝突,請重新整理後再試",
  422: "資料驗證未通過,請確認欄位",
  429: "操作太頻繁,請稍後再試",
};

/**
 * 業務性 4xx 可信任呈現的後端訊息:
 * - { message: string }(既有慣例)
 * - ProblemDetails { Detail / detail: string }(ASP.NET 標準;swagger 37 支 400 用這個)
 * - string[](Backend v1.00 的 Create/Update/ChangePassword 逐欄驗證),以換行合併
 * 純字串 body 另在 translateApiError 處理(要先過濾技術訊息)。
 */
function extractBusinessMessage(data) {
  if (data && typeof data.message === "string") return data.message.trim();
  const detail = data?.Detail ?? data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (Array.isArray(data) && data.length && data.every((s) => typeof s === "string")) {
    return data.map((s) => s.trim()).filter(Boolean).join("\n");
  }
  return "";
}

/**
 * @param {any} error axios 攔截器收到的錯誤物件
 * @returns {string} 可安全顯示給使用者的訊息
 */
export function translateApiError(error) {
  // 1) 沒有 response = 網路層錯誤(斷線/逾時/CORS…),不可能有可信 body
  if (!error || !error.response) {
    if (error && (error.code === "ECONNABORTED" || /timeout/i.test(error.message || ""))) {
      return TIMEOUT_MSG;
    }
    return NETWORK_ERROR_MSG;
  }

  const { status } = error.response;
  const backendMsg = extractBusinessMessage(error.response.data);

  // 2) 5xx:一律制式,絕不顯示後端 body。
  // 例外 501:USE_MOCK=false 時未串接模組的前端守衛(mock/notImplemented)用它
  // 誠實說明,不能講成「系統忙碌」誤導使用者以為是後端壞了
  if (status === 501) return NOT_IMPLEMENTED_MSG;
  if (status >= 500) return "系統忙碌中,請稍後再試";

  // 3) 業務性 4xx:採用後端刻意提供的訊息(如刪除阻擋原因)
  if (TRUST_BACKEND_MESSAGE.has(status) && backendMsg) return backendMsg;
  // 3b) 純字串 body(例:「資產編號『X』已存在」「找不到資產編號欄」):採用,但去標籤、截斷,
  //     長得像例外/SQL 的不採用,避免 stack trace 直出。
  //     以前只信 swagger 宣告字串 body 的資源(呼叫端標 trustStringError),軟體／機櫃回同一句
  //     只剩「操作衝突」→ 2026-10-21 使用者裁示:這種原因一律顯示,不分資源。
  if (TRUST_BACKEND_MESSAGE.has(status) && typeof error.response.data === "string") {
    // 結尾的「(id=8387)」是資料庫內部編號,使用者用不到(SIT DcHardware 409 實測,10/21 裁示不顯示)。
    // 只認結尾、半形括號、id=數字;後端換寫法就比對不到 → 原文照顯示,不會吃掉原因
    const text = error.response.data.replace(/<[^>]+>/g, "").trim().replace(/\s*\(id=\d+\)$/i, "");
    // 長得像例外／技術訊息就不採用,退回制式文案(安全審查 2026-10-17:4xx 字串也可能夾 stack trace)
    if (text && !LOOKS_TECHNICAL.test(text)) return text.length > 300 ? `${text.slice(0, 300)}…` : text;
  }

  // 4) 其餘依狀態制式文案
  return BY_STATUS[status] || GENERIC;
}

export default translateApiError;
